import {
    ActionIcon,
    Button,
    Divider,
    Group,
    Popover,
    SimpleGrid,
    Stack,
    Text,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import {
    IconCalendarEvent,
    IconChevronDown,
    IconChevronLeft,
    IconChevronRight,
    IconX,
} from "@tabler/icons-react";
import { useState } from "react";
import { DATE_PRESETS, datePresetRange } from "./services/datePresets";
import { monthRange, quarterRange, yearRange } from "./services/dateRanges";
import type { DateRangeValue } from "./types";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const QUARTERS = [1, 2, 3, 4];

const formatDay = (date: Date): string =>
    date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const describeRange = (value: DateRangeValue): string | undefined => {
    const [start, end] = value;
    if (!start) return undefined;
    return end ? `${formatDay(start)} – ${formatDay(end)}` : formatDay(start);
};

const rangeKey = (range: DateRangeValue): string =>
    range.map((date) => date?.getTime() ?? 0).join("|");

export const OverviewDateFilter = ({
    value,
    onChange,
    label,
}: {
    value: DateRangeValue;
    onChange: (value: DateRangeValue) => void;
    label: string;
}) => {
    const [opened, setOpened] = useState(false);
    const [custom, setCustom] = useState(false);
    const [year, setYear] = useState(() => value[0]?.getFullYear() ?? new Date().getFullYear());
    const summary = describeRange(value);

    const apply = (range: [Date, Date]) => {
        onChange(range);
        setOpened(false);
    };

    const isActive = (range: [Date, Date]): boolean => rangeKey(value) === rangeKey(range);

    const open = () => {
        if (value[0]) setYear(value[0].getFullYear());
        setCustom(false);
        setOpened(true);
    };

    return (
        <Popover
            opened={opened}
            onChange={setOpened}
            position="bottom-end"
            shadow="md"
            radius="md"
            width={300}
        >
            <Popover.Target>
                <Button
                    variant="default"
                    justify="space-between"
                    w={290}
                    maw="100%"
                    leftSection={<IconCalendarEvent size={16} style={{ color: "#8b73d6" }} />}
                    rightSection={<IconChevronDown size={15} style={{ color: "#b9abe6" }} />}
                    styles={{
                        root: { borderColor: "#e2d5fb" },
                        label: { fontWeight: 500, color: summary ? "#2b2536" : "#868e96" },
                    }}
                    onClick={() => (opened ? setOpened(false) : open())}
                >
                    {summary ?? `Filter by ${label}`}
                </Button>
            </Popover.Target>
            <Popover.Dropdown p="sm">
                {custom ? (
                    <Stack gap="xs">
                        <Group justify="space-between">
                            <Text fw={700} size="sm">
                                Custom range
                            </Text>
                            <Button variant="subtle" size="xs" onClick={() => setCustom(false)}>
                                Back
                            </Button>
                        </Group>
                        <DatePicker
                            type="range"
                            allowSingleDateInRange
                            value={value}
                            onChange={onChange}
                        />
                    </Stack>
                ) : (
                    <Stack gap={8}>
                        <Group gap="xs">
                            {DATE_PRESETS.map((option) => (
                                <Button
                                    key={option.preset}
                                    size="xs"
                                    variant="subtle"
                                    color="violet"
                                    onClick={() =>
                                        apply(
                                            datePresetRange({
                                                preset: option.preset,
                                                today: new Date(),
                                            }),
                                        )
                                    }
                                >
                                    {option.label}
                                </Button>
                            ))}
                        </Group>
                        <Divider />
                        <Group justify="center" gap="md">
                            <ActionIcon variant="subtle" onClick={() => setYear((y) => y - 1)}>
                                <IconChevronLeft size={16} />
                            </ActionIcon>
                            <Text fw={700}>{year}</Text>
                            <ActionIcon variant="subtle" onClick={() => setYear((y) => y + 1)}>
                                <IconChevronRight size={16} />
                            </ActionIcon>
                        </Group>
                        <Button
                            variant={isActive(yearRange(year)) ? "filled" : "light"}
                            color="violet"
                            size="xs"
                            onClick={() => apply(yearRange(year))}
                        >
                            Full year {year}
                        </Button>
                        <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                            Quarters
                        </Text>
                        <SimpleGrid cols={4} spacing="xs">
                            {QUARTERS.map((quarter) => (
                                <Button
                                    key={quarter}
                                    variant={
                                        isActive(quarterRange({ year, quarter }))
                                            ? "filled"
                                            : "default"
                                    }
                                    color="violet"
                                    size="xs"
                                    onClick={() => apply(quarterRange({ year, quarter }))}
                                >
                                    Q{quarter}
                                </Button>
                            ))}
                        </SimpleGrid>
                        <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                            Months
                        </Text>
                        <SimpleGrid cols={4} spacing="xs">
                            {MONTHS.map((monthLabel, month) => (
                                <Button
                                    key={monthLabel}
                                    variant={
                                        isActive(monthRange({ year, month })) ? "filled" : "default"
                                    }
                                    color="violet"
                                    size="xs"
                                    onClick={() => apply(monthRange({ year, month }))}
                                >
                                    {monthLabel}
                                </Button>
                            ))}
                        </SimpleGrid>
                        <Divider />
                        <Group justify="space-between">
                            <Button
                                variant="subtle"
                                size="xs"
                                leftSection={<IconCalendarEvent size={15} />}
                                onClick={() => setCustom(true)}
                            >
                                Custom range…
                            </Button>
                            {summary && (
                                <Button
                                    variant="subtle"
                                    color="gray"
                                    size="xs"
                                    leftSection={<IconX size={14} />}
                                    onClick={() => {
                                        onChange([null, null]);
                                        setOpened(false);
                                    }}
                                >
                                    Clear
                                </Button>
                            )}
                        </Group>
                    </Stack>
                )}
            </Popover.Dropdown>
        </Popover>
    );
};
